export type InventoryWarning =
  | 'null_quantity'
  | 'zero_quantity'
  | 'negative_quantity'
  | 'orphan_product'
  | 'missing_warehouse';

export interface InventorySearch {
  q?: string;
  warehouseId?: string;
  warning?: string;
  limit?: number;
}

export const DEFAULT_INVENTORY_LIMIT = 50;
export const MAX_INVENTORY_QUERY_LENGTH = 200;
const SOURCE_ID = /^(0|[1-9][0-9]*)$/u;
const WARNINGS: readonly InventoryWarning[] = [
  'null_quantity',
  'zero_quantity',
  'negative_quantity',
  'orphan_product',
  'missing_warehouse',
];

/** Retain malformed deep-link values as a visible invalid-query state; never send them to the API. */
export function parseInventorySearch(search: Record<string, unknown>): InventorySearch {
  return {
    q: search.q === undefined ? undefined : typeof search.q === 'string' ? search.q : '\u0000',
    warehouseId:
      search.warehouseId === undefined
        ? undefined
        : typeof search.warehouseId === 'string'
          ? search.warehouseId
          : typeof search.warehouseId === 'number' &&
              Number.isSafeInteger(search.warehouseId) &&
              search.warehouseId >= 0
            ? String(search.warehouseId)
            : '\u0000',
    warning:
      search.warning === undefined
        ? undefined
        : typeof search.warning === 'string'
          ? search.warning
          : '\u0000',
    limit:
      search.limit === undefined
        ? undefined
        : typeof search.limit === 'number'
          ? search.limit
          : Number.NaN,
  };
}

export function isInventorySearchValid(search: InventorySearch): boolean {
  const q = (search.q ?? '').trim();
  const warehouseId = search.warehouseId ?? '';
  const warning = search.warning ?? '';
  const limit = search.limit ?? DEFAULT_INVENTORY_LIMIT;
  return (
    q.length <= MAX_INVENTORY_QUERY_LENGTH &&
    !q.includes('\u0000') &&
    (warehouseId === '' || (warehouseId.length <= 100 && SOURCE_ID.test(warehouseId))) &&
    (warning === '' || WARNINGS.some((candidate) => candidate === warning)) &&
    Number.isInteger(limit) &&
    limit >= 1 &&
    limit <= 100
  );
}

export function asInventoryWarning(value: string | undefined): InventoryWarning | undefined {
  return WARNINGS.find((candidate) => candidate === value);
}
